import React, { useCallback, useMemo, type ReactNode } from 'react';
import { styled, css } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { Flex } from '@atlaskit/primitives';
import { AsyncSelect } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { selectInAModalStyleFixProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import { toAccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { User } from '@atlassian/jira-user-picker-next/src/services/fetch-users/types.tsx';
import type { UserOption } from '../common/types.tsx';
import { createUseUsersHook } from '../services/index.tsx';

export type Props = {
	isMultiUserPicker: boolean;
	hasValidationError?: boolean;
	value: UserOption[];
	scopedGroups: string[];
	autoFocus: boolean;
	isClearable?: boolean;
	onChange: (arg1: UserOption[]) => void;
	shouldInputRenderValue?: boolean;
	menuIsOpen?: boolean;
	isDisabled?: boolean;
};

export const UserAttributeUserPicker = (props: Props) => {
	const {
		menuIsOpen,
		isMultiUserPicker,
		value,
		scopedGroups,
		hasValidationError,
		onChange,
		autoFocus,
	} = props;

	// selectedUsers is used by the created useUsers hook to ensure that the
	// currently selected options are always present in the available options of
	// the user picker component
	const selectedUsers: User[] = useMemo(
		() =>
			value.map(({ accountId, avatarUrl, displayName, accountType }) => ({
				avatarUrl,
				fixed: false,
				id: accountId,
				key: accountId,
				name: displayName,
				type: 'user',
				accountType,
			})),
		[value],
	);

	const useUsers = createUseUsersHook({ groups: scopedGroups, selectedUsers });

	const [fetchData] = useUsers();

	const loadPickerOptions = useCallback(
		(query: string, cb?: (arg1: UserOption[]) => void) => {
			fetchData({ query }).then((response) => {
				const fetchedOptions = response?.map(({ id, name, avatarUrl }) => ({
					accountId: toAccountId(id),
					displayName: name,
					avatarUrl,
				}));
				cb?.(fetchedOptions ?? []);
			});
		},
		[fetchData],
	);

	const [debouncedLoadOptions] = useDebouncedCallback(
		(query: string, callback?: (arg1: UserOption[] | undefined) => void) =>
			loadPickerOptions(query, callback),
		400,
	);
	const formatOptionLabel = ({ displayName, avatarUrl }: UserOption) => (
		<OptionInnerWrapper>
			{avatarUrl && <Avatar appearance="circle" size="xsmall" src={avatarUrl} />}
			<OptionName>{displayName}</OptionName>
		</OptionInnerWrapper>
	);

	return (
		<UserPickerBorderOverride hasValidationError={hasValidationError}>
			<AsyncSelect
				{...selectInAModalStyleFixProps}
				isDisabled={props.isDisabled}
				isClearable={props.isClearable}
				controlShouldRenderValue={props.shouldInputRenderValue}
				defaultOptions
				formatOptionLabel={formatOptionLabel}
				autoFocus={autoFocus}
				menuIsOpen={menuIsOpen}
				menuPlacement="auto"
				value={value}
				getOptionLabel={(userOption) => userOption.displayName}
				getOptionValue={(userOption) => userOption.accountId}
				isMulti={isMultiUserPicker}
				loadOptions={debouncedLoadOptions}
				onChange={(newUsers) => {
					if (newUsers == null) {
						props.onChange([]);
					} else {
						const users = Array.isArray(newUsers) ? newUsers : [newUsers];
						onChange(users);
					}
				}}
				validationState={hasValidationError ? 'error' : 'default'}
				classNamePrefix="insight-user-attribute-select"
			/>
		</UserPickerBorderOverride>
	);
};

UserAttributeUserPicker.defaultProps = {
	hasValidationError: false,
	autoFocus: false,
	isClearable: true,
	shouldInputRenderValue: true,
};

const redBorder = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&& div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderColor: token('color.border.danger', colors.R400),
	},
});

const OptionInnerWrapper = ({ children }: { children: ReactNode }) => (
	<Flex alignItems="center">{children}</Flex>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionName = styled.span({
	placeSelf: 'center',
	paddingLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const UserPickerBorderOverride = styled.div<{ hasValidationError?: boolean }>(
	{},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	({ hasValidationError }) => (hasValidationError ? redBorder : ''),
);
